import { FC, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useIntercom } from 'react-use-intercom';
import { useOutsideClickDetection } from '../../../hooks/UseOutsideClickDetection';
import { AppState } from '../../../store/AppState';
import { CurrentUser } from '../../../types';

type IntercomProps = {
  unreadMessagesCount: number;
};

const Intercom: FC<IntercomProps> = ({ unreadMessagesCount }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { boot, hide, update } = useIntercom();
  const { firstName, lastName, userName, dealerId, jobTitle, intercomUserHMAC } = useSelector<
    AppState,
    CurrentUser
  >((state) => state.currentUser);

  useOutsideClickDetection(wrapperRef, hide);

  const bootWithProps = (): void => {
    boot({
      name: `${firstName} ${lastName}`,
      email: userName,
      userHash: intercomUserHMAC,
      customAttributes: {
        DealerId: dealerId,
        title: jobTitle,
      },
      customLauncherSelector: '.navbar-intercom',
      hideDefaultLauncher: true,
    });
  };

  useEffect(() => {
    // The history API maintains the navigation state.
    // Whenever a new page is navigated to,'history.pushState' is called and the page is added to the state.
    // That means this event 'history.pushState' is called whenever the URL changes, and we can use it to
    // execute custom code whenever the url changes.
    // reference: https://itsopensource.com/how-to-call-a-function-on-URL-change-in-javascript/
    (function (history) {
      const pushState = history.pushState;
      history.pushState = function (state) {
        // update intercom on every URL change to ping for new messages and set users last request at
        update({
          name: `${firstName} ${lastName}`,
          email: userName,
          userHash: intercomUserHMAC,
          customAttributes: {
            DealerId: dealerId,
            Title: jobTitle,
          },
          lastRequestAt: Math.floor(new Date().getTime() / 1000),
        });

        return pushState.apply(history, arguments);
      };
    })(window.history);
  }, []);

  return (
    <div ref={wrapperRef} data-testid='intercom-launcher'>
      <i
        className='am-icon-help-circle-full navbar-icon #intercom-launcher-icon'
        onClick={bootWithProps}
      />
      {unreadMessagesCount > 0 && <span className='notif-badge'>{unreadMessagesCount}</span>}
    </div>
  );
};

export default Intercom;
